import React from "react";
import { PageHeader } from "@shared/components/PageHeader";
import { HelpCircle, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";

// Define reading condition types and their associated colors
type ReadingCondition = "Normal" | "Faulty" | "RCNT";

interface ReadingConditionData {
  condition: ReadingCondition;
  count: number;
  percentage: string;
  dotColor: string;
  barColor: string;
}

export const MeterReadingQuality = ({data}: {data: any}) => {
  // Mock data - in a real implementation this would come from props or an API
  const readingData: ReadingConditionData[] = [
    {
      condition: "Normal",
      count: data?.readingStats?.normalCount || 0,
      percentage: `(${data?.readingStats?.normalPercentage || 0}%)`,
      dotColor: "#00a67e",
      barColor: "#c8f5e7",
    },
    {
      condition: "Faulty",
      count: data?.readingStats?.faultyCount || 0,
      percentage: `(${data?.readingStats?.faultyPercentage || 0}%)`,
      dotColor: "#ff8000",
      barColor: "#fff0d9",
    },
    {
      condition: "RCNT",
      count: data?.readingStats?.rcntCount || 0,
      percentage: `(${data?.readingStats?.rcntPercentage || 0}%)`,
      dotColor: "#ff0000",
      barColor: "#ffecec",
    },
  ];

  return (
    <div className="w-full">
      <div className="flex justify-between items-center">
        <PageHeader
          title="Meter Reading Quality"
          subtitle="Counts by reading condition in this cycle."
        />
        <Tooltip>
          <TooltipTrigger>
            <HelpCircle className="h-4 w-4 text-gray-400" />
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-xs">
              Use quality to identify asset and process issues impacting KPIs.
            </p>
          </TooltipContent>
        </Tooltip>
      </div>

      <div className="space-y-6 mt-4">
        {readingData.map((item) => (
          <div key={item.condition} className="mb-4">
            <div className="flex justify-between items-center mb-1">
              <div className="flex items-center">
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: item.dotColor }}
                />
                <span>{item.condition}</span>
              </div>
              <span>
                {item.count} {item.percentage}
              </span>
            </div>
     <div
              className="w-full h-2 rounded-full relative"
              style={{ backgroundColor: item.barColor }}
            >
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ 
                  backgroundColor: item.dotColor,
                  width: item.percentage.replace(/[()%]/g, '') + '%'
                }}
              />
            </div>
          </div>
        ))}

        <p className="mt-6 text-xs text-gray-500">
          High faulty/RCNT shares can reduce completion and accuracy. Use these
          indicators to plan maintenance and targeted revisits.
        </p>
      </div>
    </div>
  );
};
